
import React from 'react';
import { Link } from 'react-router-dom';

const NetworkSegmentation: React.FC = () => {
  return (
    <article className="max-w-4xl mx-auto prose prose-lg">
      <section>
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          <strong>Network segmentation for small business</strong> is one of the most effective ways to improve 
          security, performance, and compliance. By separating corporate workstations, guest Wi-Fi, and IoT devices 
          into isolated network segments, you can prevent security breaches, reduce network congestion, and maintain 
          better control over your business network infrastructure.
        </p>
        
        <p className="mb-6 text-gray-700 leading-relaxed">
          In this comprehensive <strong>VLAN configuration guide</strong>, we'll walk you through the essential 
          concepts and practical steps to implement effective network segmentation using modern networking equipment 
          like UniFi systems.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-itmgmt-dark-blue mb-6">What is Network Segmentation and Why Does Your Business Need It?</h2>
        
        <p className="mb-4 text-gray-700 leading-relaxed">
          <strong>Network segmentation</strong> is the practice of dividing a computer network into smaller, 
          isolated sub-networks called segments or VLANs (Virtual Local Area Networks). Each segment operates 
          independently, with controlled communication between segments based on predefined security policies.
        </p>
        
        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">Critical Security Benefits</h3>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li><strong>Breach Containment:</strong> If one segment is compromised, attackers cannot easily move laterally to other network areas</li>
          <li><strong>Regulatory Compliance:</strong> Meet industry requirements for data separation (PCI DSS, HIPAA, SOX)</li>
          <li><strong>Access Control:</strong> Restrict sensitive resources to authorized users and devices only</li>
          <li><strong>Threat Isolation:</strong> Contain malware, ransomware, and other security incidents</li>
        </ul>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">Performance and Management Advantages</h3>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li><strong>Reduced Network Congestion:</strong> Separate bandwidth-heavy applications from critical business traffic</li>
          <li><strong>Quality of Service (QoS):</strong> Prioritize important traffic like VoIP and video conferencing</li>
          <li><strong>Simplified Troubleshooting:</strong> Isolate network issues to specific segments</li>
          <li><strong>Scalable Growth:</strong> Add new devices and users without compromising existing network performance</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-itmgmt-dark-blue mb-6">Essential Network Segments Every Small Business Needs</h2>
        
        <p className="mb-6 text-gray-700 leading-relaxed">
          A well-designed <strong>business network security</strong> strategy typically includes these core segments:
        </p>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">1. Corporate/Production Network</h3>
        <div className="bg-blue-50 p-4 rounded-lg mb-4">
          <p className="text-gray-700 mb-2"><strong>Purpose:</strong> Employee workstations, servers, and critical business applications</p>
          <p className="text-gray-700 mb-2"><strong>Access Level:</strong> Highest security, authenticated users only</p>
          <p className="text-gray-700"><strong>Typical VLAN ID:</strong> 10-20</p>
        </div>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">2. Guest Network</h3>
        <div className="bg-green-50 p-4 rounded-lg mb-4">
          <p className="text-gray-700 mb-2"><strong>Purpose:</strong> Visitor internet access without corporate network access</p>
          <p className="text-gray-700 mb-2"><strong>Access Level:</strong> Internet only, no internal resources</p>
          <p className="text-gray-700"><strong>Typical VLAN ID:</strong> 30-40</p>
        </div>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">3. IoT/Smart Device Network</h3>
        <div className="bg-orange-50 p-4 rounded-lg mb-4">
          <p className="text-gray-700 mb-2"><strong>Purpose:</strong> Security cameras, smart thermostats, printers, and other connected devices</p>
          <p className="text-gray-700 mb-2"><strong>Access Level:</strong> Limited internet access, restricted internal communication</p>
          <p className="text-gray-700"><strong>Typical VLAN ID:</strong> 50-60</p>
        </div>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">4. DMZ (Demilitarized Zone)</h3>
        <div className="bg-red-50 p-4 rounded-lg mb-6">
          <p className="text-gray-700 mb-2"><strong>Purpose:</strong> Public-facing servers, web applications, email servers</p>
          <p className="text-gray-700 mb-2"><strong>Access Level:</strong> Controlled external access, isolated from internal network</p>
          <p className="text-gray-700"><strong>Typical VLAN ID:</strong> 70-80</p>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-itmgmt-dark-blue mb-6">UniFi Network Segmentation: Step-by-Step VLAN Configuration Guide</h2>
        
        <p className="mb-6 text-gray-700 leading-relaxed">
          <strong>UniFi network segmentation</strong> provides enterprise-grade capabilities at small business prices. 
          Here's how to configure VLANs using Ubiquiti's UniFi system:
        </p>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">Step 1: Plan Your Network Architecture</h3>
        <ol className="list-decimal pl-6 mb-6 space-y-2 text-gray-700">
          <li>Document all devices and their required access levels</li>
          <li>Assign VLAN IDs for each network segment</li>
          <li>Define firewall rules between segments</li>
          <li>Plan IP address ranges (e.g., 192.168.10.x for corporate, 192.168.30.x for guests)</li>
        </ol>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">Step 2: Configure VLANs in UniFi Controller</h3>
        <ol className="list-decimal pl-6 mb-6 space-y-2 text-gray-700">
          <li>Log into your UniFi Network Controller</li>
          <li>Navigate to Settings → Networks</li>
          <li>Click "Create New Network"</li>
          <li>Configure each VLAN with appropriate settings:
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Network name and VLAN ID</li>
              <li>DHCP range and gateway</li>
              <li>DNS servers</li>
              <li>Guest policy (if applicable)</li>
            </ul>
          </li>
        </ol>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">Step 3: Create WiFi Networks for Each Segment</h3>
        <ol className="list-decimal pl-6 mb-6 space-y-2 text-gray-700">
          <li>Go to Settings → WiFi</li>
          <li>Create separate SSIDs for each network segment</li>
          <li>Assign each WiFi network to its corresponding VLAN</li>
          <li>Configure appropriate security settings (WPA3-Personal/Enterprise)</li>
        </ol>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">Step 4: Configure Switch Port Profiles</h3>
        <ol className="list-decimal pl-6 mb-6 space-y-2 text-gray-700">
          <li>Navigate to Settings → Profiles → Switch Ports</li>
          <li>Create profiles for different device types:
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Corporate devices (tagged/untagged as needed)</li>
              <li>IoT devices</li>
              <li>Access points (trunk ports with all VLANs)</li>
            </ul>
          </li>
        </ol>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-itmgmt-dark-blue mb-6">Firewall Rules and Security Policies for Network Isolation</h2>
        
        <p className="mb-4 text-gray-700 leading-relaxed">
          Proper <strong>network isolation SMB</strong> requires well-configured firewall rules. Here are essential 
          security policies to implement:
        </p>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">Inter-VLAN Traffic Rules</h3>
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li><strong>Guest → Corporate:</strong> DENY ALL (guests cannot access internal resources)</li>
            <li><strong>IoT → Corporate:</strong> DENY ALL (except specific services like print servers)</li>
            <li><strong>Corporate → IoT:</strong> ALLOW ESTABLISHED (for management and monitoring)</li>
            <li><strong>All → Internet:</strong> ALLOW (with content filtering as needed)</li>
          </ul>
        </div>

        <p className="mb-6 text-gray-700 leading-relaxed">
          For comprehensive firewall configuration and ongoing security monitoring, consider partnering with a{' '}
          <Link to="/services/managed-it-services" className="text-itmgmt-bright-blue hover:underline">managed IT services provider</Link>{' '}
          who can ensure your network segmentation remains effective and up-to-date.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-itmgmt-dark-blue mb-6">Common Network Segmentation Mistakes to Avoid</h2>
        
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <h3 className="text-lg font-semibold text-red-800 mb-2">⚠️ Critical Errors That Compromise Security</h3>
          <ul className="list-disc pl-6 space-y-2 text-red-700">
            <li><strong>Default VLAN Usage:</strong> Never leave devices on VLAN 1 (default)</li>
            <li><strong>Overly Permissive Rules:</strong> Start with deny-all and add specific allow rules</li>
            <li><strong>Unmanaged Switches:</strong> Use managed switches that support VLANs throughout your network</li>
            <li><strong>Poor Documentation:</strong> Maintain current network diagrams and VLAN assignments</li>
            <li><strong>Skipping Testing:</strong> Always test connectivity and isolation after changes</li>
          </ul>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-itmgmt-dark-blue mb-6">Monitoring and Maintaining Your Segmented Network</h2>
        
        <p className="mb-4 text-gray-700 leading-relaxed">
          Successful network segmentation requires ongoing monitoring and maintenance:
        </p>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">Essential Monitoring Practices</h3>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li><strong>Traffic Analysis:</strong> Monitor inter-VLAN communication patterns</li>
          <li><strong>Anomaly Detection:</strong> Set up alerts for unusual cross-segment traffic</li>
          <li><strong>Access Logging:</strong> Track which devices access which resources</li>
          <li><strong>Performance Metrics:</strong> Monitor bandwidth usage per segment</li>
        </ul>

        <p className="mb-6 text-gray-700 leading-relaxed">
          For businesses requiring robust{' '}
          <Link to="/services/cybersecurity" className="text-itmgmt-bright-blue hover:underline">cybersecurity solutions</Link>, 
          professional network monitoring and incident response services can provide 24/7 oversight of your 
          segmented network infrastructure.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-itmgmt-dark-blue mb-6">Next Steps: Implementing Network Segmentation in Your Business</h2>
        
        <p className="mb-4 text-gray-700 leading-relaxed">
          Ready to enhance your business network security with proper segmentation? Here's your action plan:
        </p>

        <ol className="list-decimal pl-6 mb-6 space-y-3 text-gray-700">
          <li><strong>Assessment:</strong> Audit your current network topology and identify security gaps</li>
          <li><strong>Planning:</strong> Design your VLAN structure based on business requirements</li>
          <li><strong>Equipment:</strong> Ensure you have managed switches and enterprise-grade access points</li>
          <li><strong>Implementation:</strong> Follow the step-by-step configuration guide above</li>
          <li><strong>Testing:</strong> Verify isolation and connectivity before going live</li>
          <li><strong>Documentation:</strong> Create network diagrams and configuration backups</li>
        </ol>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">Professional Implementation Available</h3>
          <p className="text-blue-700">
            Need expert help implementing network segmentation? Our team specializes in designing and deploying 
            secure, scalable network architectures for small and medium businesses. We handle everything from 
            initial planning to ongoing management and monitoring.
          </p>
        </div>

        <h3 className="text-xl font-semibold text-itmgmt-dark-blue mb-4">Additional Resources</h3>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li>
            <a href="https://help.ui.com/hc/en-us/articles/219654087-UniFi-Using-VLANs-with-UniFi-Wireless-Routing-Switching-Hardware" 
               target="_blank" 
               rel="noopener noreferrer"
               className="text-itmgmt-bright-blue hover:underline">
              Ubiquiti Official VLAN Configuration Guide
            </a>
          </li>
          <li>
            <a href="https://www.cisco.com/c/en/us/support/docs/lan-switching/vlan/10023-4.html" 
               target="_blank" 
               rel="noopener noreferrer"
               className="text-itmgmt-bright-blue hover:underline">
              Cisco VLAN Configuration Best Practices
            </a>
          </li>
        </ul>
      </section>
    </article>
  );
};

export default NetworkSegmentation;
